import { AfterViewInit, Component, inject, Inject, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { GuardsCheckEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { ErrorAlertComponent, GlobalComponentService, IGlobalComponent, IndeterminateProgressBarComponent, InfoAlertComponent, InfoConfirmComponent, TmModalCloseReason } from '@techmore/global';
import { Response } from 'express';
import { merge, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { RESPONSE } from '../express.tokens';
import { ServerConfigService } from './share/service/server-config.service';
import { IS_BROWSER_TOKEN } from './share/tokens/is-browser.token';
import { IS_SERVER_TOKEN } from './share/tokens/is-server.token';
import { removeUrlQueryParam } from './util';
import { LoggerService } from './share/service/logger.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy, IGlobalComponent {
  @ViewChild(InfoAlertComponent) infoAlert: InfoAlertComponent;
  @ViewChild(InfoConfirmComponent) infoConfirm: InfoConfirmComponent;
  @ViewChild(ErrorAlertComponent) errorAlert: ErrorAlertComponent;
  @ViewChild(IndeterminateProgressBarComponent) indeterminateProgressBar: IndeterminateProgressBarComponent;
  defaultInfoTitle = '';
  defaultInfoAlertTitle = '';
  defaultErrorAlertTitle = '';
  private readonly serverConfigService = inject(ServerConfigService);
  private readonly loggerService = inject(LoggerService);
  isBrowser = inject(IS_BROWSER_TOKEN);
  isServer = inject(IS_SERVER_TOKEN);
  ngUnsub$ = new Subject<void>();
  constructor(
    private readonly globalComponentService: GlobalComponentService,
    private readonly router: Router,
    @Optional() @Inject(RESPONSE) private readonly response: Response,
  ) {
    // if (this.isBrowser) {
    //   if (history.scrollRestoration) {
    //     history.scrollRestoration = 'manual';
    //   } else {
    //     window.onbeforeunload = () => {
    //       // console.log('onbeforeunload window.scrollY 1 : ' + window.scrollY);
    //       window.scrollTo(0, 0);
    //     };
    //   }
    // }
  }

  ngOnInit(): void {
    this.globalComponentService.globalCmp = this;

    this.router.events.pipe(
      filter(evt => evt instanceof NavigationError),
      takeUntil(this.ngUnsub$)
    ).subscribe((evt: NavigationError) => {
      this.loggerService.error('router errorHandler', evt);
      if (this.isServer) {
        this.response?.redirect(301, `${this.getNotfoundUrl()}`);
      } else {
        // or redirect to default route
        window.location.href = `${window.location.origin}${this.getNotfoundUrl()}`;
      }
    })

    let isUrlPathChange = false;
    this.router.events.pipe(
      filter(evt => evt instanceof GuardsCheckEnd || evt instanceof NavigationStart),
      takeUntil(this.ngUnsub$)
    ).subscribe((evt) => {
      if (evt instanceof NavigationStart) {
        isUrlPathChange = removeUrlQueryParam(this.router.url.replace('.html','')) !== removeUrlQueryParam(evt.url.replace('.html',''));

        if (this.isBrowser && isUrlPathChange) {
          window.location.href = `${window.location.origin}${evt['url']}`;
        }
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.defaultInfoTitle = this.infoConfirm.title;
      this.defaultInfoAlertTitle = this.infoAlert.title;
      this.defaultErrorAlertTitle = this.errorAlert.title;
    }
  }

  ngOnDestroy(): void {
    this.ngUnsub$.next();
    this.ngUnsub$.complete();
  }

  openInfoAlert(message: string, param?: { title?: string, afterClose?: () => void }) {
    this.infoAlert!.message = message;
    if (!this.isBrowser) { return this.infoAlert as InfoAlertComponent; }
    if (param) {
      this.infoAlert!.title = param.title || this.defaultInfoAlertTitle;
      if (param.afterClose) {
        let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
        merge(
          this.infoAlert!.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
          this.infoAlert!.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
        ).pipe(take(1))
          .subscribe(() => {
            (param.afterClose as () => void)();
          });
      }
    }

    this.infoAlert!.open();
    return this.infoAlert as InfoAlertComponent;
  }

  openInfoConfirm(message: string, param?: { title?: string, afterConfirm?: () => void, afterClose?: () => void }) {
    this.infoConfirm!.message = message;
    if (!this.isBrowser) { return this.infoConfirm as InfoConfirmComponent; }
    if (param) {
      this.infoConfirm!.title = param.title || this.defaultInfoTitle;
      if (param.afterConfirm || param.afterClose) {
        let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
        merge(
          this.infoConfirm!.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
          this.infoConfirm!.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
        ).pipe(take(1))
          .subscribe(() => {
            if (closeReason === TmModalCloseReason.SUBMIT) {
              (param.afterConfirm as () => void)();
            } else {
              (param.afterClose as () => void)();
            }
          });
      }
    }

    this.infoConfirm!.open();
    return this.infoConfirm as InfoConfirmComponent;
  }

  openErrorAlert(message: string, param?: { title?: string, afterConfirm?: () => void, afterClose?: () => void, showCopyTip?: boolean }) {
    this.errorAlert!.message = message;
    if (!this.isBrowser) { return this.errorAlert as ErrorAlertComponent; }
    if (param) {
      this.errorAlert!.title = param.title || this.defaultErrorAlertTitle;
      let closeReason: TmModalCloseReason = TmModalCloseReason.SUBMIT;
      merge(
        this.errorAlert!.afterConfirm.pipe(tap(() => { closeReason = TmModalCloseReason.SUBMIT })),
        this.errorAlert!.afterCancel.pipe(tap(() => { closeReason = TmModalCloseReason.CLOSE })),
      ).pipe(take(1))
        .subscribe(() => {
          if (param.afterClose) {
            (param.afterClose as () => void)();
          }
          if (param.showCopyTip) {
            this.errorAlert!.showCopyButton = false;
          }
        });
      if (param.showCopyTip) {
        this.errorAlert!.showCopyButton = true;
      }
    }

    this.errorAlert!.open();
    return this.errorAlert as ErrorAlertComponent;
  }

  openIndeterminateProgressBar() {
    this.indeterminateProgressBar?.show();
  }

  closeIndeterminateProgressBar() {
    this.indeterminateProgressBar?.hide();
  }

  private getNotfoundUrl() {
    const isBaseHrefTrailingSlash = this.serverConfigService.baseHref.endsWith('/');
    if (isBaseHrefTrailingSlash) {
      return `${this.serverConfigService.baseHref}404.html`;
    }
    return `${this.serverConfigService.baseHref}/404.html`;
  }



}
